/**
 * PATH: packages/drafting/src/ai/runner.ts
 * FILE_ID: DRAFTING-AI-RUNNER-001
 * PURPOSE: Pure-ish orchestration for the AI generate/refine flow (DRAFTING_STUDIO
 *   _SCOPE.md §8). Sequences upload-sketch -> generate/refine and emits progress
 *   via a status callback. Extracted from the React hook so it is unit-testable
 *   with injected fake handlers (rule 05 §1.6 I->O).
 */
import type { DrawingIR } from '../ir/schema'
import type { AiResult, DraftingAiHandlers, GenerateInput } from './types'

export interface RunnerDeps extends DraftingAiHandlers {
  setStatus: (s: string | null) => void
}

export interface GenerateArgs {
  prompt: string
  sketch: File | null
  scale: string
  units: GenerateInput['units']
  measurements?: GenerateInput['measurements']
}

/** runGenerate — upload the sketch (if any), then call the host's generate. */
export async function runGenerate(deps: RunnerDeps, args: GenerateArgs): Promise<AiResult> {
  if (!deps.onGenerate) throw new Error('Generation is not available here.')
  let sketchAssetId: string | undefined
  if (args.sketch && deps.onUploadSketch) {
    deps.setStatus('Uploading sketch…')
    sketchAssetId = await deps.onUploadSketch(args.sketch)
  }
  deps.setStatus(args.sketch ? 'Reading sketch + drawing…' : 'Drawing…')
  const result = await deps.onGenerate({
    prompt: args.prompt,
    scale: args.scale,
    units: args.units,
    sketchAssetId,
    measurements: args.measurements,
  })
  deps.setStatus(statusFor(result))
  return result
}

/** runRefine — correct-by-prompt over the current IR. */
export async function runRefine(
  deps: RunnerDeps,
  args: { instruction: string; baseIr: DrawingIR },
): Promise<AiResult> {
  if (!deps.onRefine) throw new Error('Refine is not available here.')
  deps.setStatus('Refining…')
  const result = await deps.onRefine(args.instruction, args.baseIr)
  deps.setStatus(statusFor(result))
  return result
}

/** statusFor — terminal message; warnings never block (the validator gates
 *  export/send, not the draft itself — DRAFTING_STUDIO_SCOPE.md §8.2). */
export function statusFor(result: AiResult): string {
  return result.ok ? 'Drawing ready' : 'Generated with issues — review the flagged items'
}
